import {
  MutableRefObject,
  useState,
  useCallback,
  useContext,
  useRef,
  useEffect,
} from 'react';

import fscreen from 'fscreen';

import { Present, TrackPresentModeFn } from '/types';
import { AppShellContext } from '/utils/contexts';
import { isViewedBlock } from '/utils/functions';

type UsePresentMode = (
  ref: MutableRefObject<HTMLElement[]>,
  trackPresentMode: TrackPresentModeFn,
) => {
  presentMode: Present;
  handlePresent: () => void;
  onScreenBlock: MutableRefObject<string | undefined>;
  presentationRef: MutableRefObject<Element | undefined>;
};

const usePresentMode: UsePresentMode = (ref, trackPresentMode) => {
  const [presentMode, setPresentMode] = useState(Present.Off);
  const onScreenBlock: MutableRefObject<string | undefined> = useRef();
  const presentationRef = useRef<Element>();

  useEffect(() => {
    const fullscreenHandler: () => void = () => {
      if (fscreen.fullscreenElement) {
        document
          .getElementById(onScreenBlock.current || '')
          ?.parentElement?.focus();
      } else {
        setPresentMode(Present.Off);
        document.querySelector(`#${onScreenBlock?.current}`)?.scrollIntoView();
      }
    };

    fscreen.addEventListener('fullscreenchange', fullscreenHandler);

    return (): void =>
      fscreen.removeEventListener('fullscreenchange', fullscreenHandler);
  }, [setPresentMode, onScreenBlock]);

  const { accessDetails, userDetails } = useContext(AppShellContext);
  const email = userDetails && userDetails.email;
  const accessTokenEmail = accessDetails?.mntl && accessDetails.mntl.mail;
  const handlePresent = useCallback(
    (fulltext = false): void => {
      if (email && accessTokenEmail === email) {
        trackPresentMode(
          fulltext,
          email,
          window.screen.width,
          window.screen.height,
        );
      }
      const currentBlockId = ref.current
        ?.find((block) => isViewedBlock(block))
        ?.querySelector('section')?.id;
      if (currentBlockId) {
        onScreenBlock.current = currentBlockId;
      }

      fscreen.requestFullscreen(presentationRef.current);
      setPresentMode(fulltext ? Present.Full : Present.Keynote);
    },
    [accessTokenEmail, email, ref, trackPresentMode],
  );

  return { presentMode, handlePresent, onScreenBlock, presentationRef };
};

export default usePresentMode;
